import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  class: "container-fluid",
  style: {"margin-top":"80px","padding-top":"40px"}
}
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "col-lg-6 text-right align-self-end" }
const _hoisted_4 = { class: "dropdown" }
const _hoisted_5 = {
  class: "dropdown-menu",
  style: {"max-height":"300px","overflow":"auto"}
}
const _hoisted_6 = { key: 0 }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "text-muted" }
const _hoisted_9 = {
  key: 0,
  class: "mt-3"
}
const _hoisted_10 = { class: "row" }
const _hoisted_11 = { class: "tile bg-color-gray5" }
const _hoisted_12 = { class: "pointer float-right" }
const _hoisted_13 = ["onClick"]
const _hoisted_14 = {
  class: "text-ellipsis fg-color-gray",
  style: {"margin-top":"7px"}
}
const _hoisted_15 = { class: "text-ellipsis fg-color-gray1" }

import { computed, onMounted, reactive, watch, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import { generateName, strCompareLowerCase } from '@common/Common'
    import WorkspaceNavigation from '@common/Components/WorkspaceNavigation.vue'

    import { DiscreteScaleEditorViewModel } from '@Scales/Components/DiscreteScaleEditor'
    import DiscreteScaleEditor from '@Scales/Components/DiscreteScaleEditor.vue'

    import type {
        DiscreteScaleReadModel,
        TchScaleTemplatesListReadModel,
        WorkspaceScalesListReadModel
    } from '@Scales/readmodels'

    import
    {
        DiscreteScaleLevelReadModel,
    } from '@Scales/readmodels'

    import
    {
        useWorkspaceScalesListQueryV1,
        useTchScaleTemplatesListQueryV1
    } from '@Scales/queries-vue-composables'

    import
    {
        useCreateWorkspaceScaleCommandV1,
        useDeleteWorkspaceScaleCommandV1,
        useUpdateWorkspaceScaleCommandV1
    } from '@Scales/commands-vue-composables'


    // ======================
    // Route
    // ======================

    
export default /*@__PURE__*/_defineComponent({
  __name: 'WorkspaceScalesPage',
  setup(__props) {

    const route = useRoute()
    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))


    // ======================
    // Queries
    // ======================

    const {
        runWorkspaceScalesListQueryV1,
        modelWorkspaceScalesListQueryV1
    } = useWorkspaceScalesListQueryV1(workspaceId)

    const rm = modelWorkspaceScalesListQueryV1 as Ref<WorkspaceScalesListReadModel | null>

    const {
        runTchScaleTemplatesListQueryV1,
        modelTchScaleTemplatesListQueryV1
    } = useTchScaleTemplatesListQueryV1(workspaceId)

    const tchTemplatesModel = modelTchScaleTemplatesListQueryV1 as Ref<TchScaleTemplatesListReadModel | null>


    // ======================
    // Commands
    // ======================

    const {
        invokeCreateWorkspaceScaleCommandV1,
        onSucceedCreateWorkspaceScaleCommandV1,
        validationErrorsCreateWorkspaceScaleCommandV1
    } = useCreateWorkspaceScaleCommandV1(workspaceId)

    const {
        invokeDeleteWorkspaceScaleCommandV1,
        onSucceedDeleteWorkspaceScaleCommandV1,
        validationErrorsDeleteWorkspaceScaleCommandV1
    } = useDeleteWorkspaceScaleCommandV1(workspaceId)

    const {
        invokeUpdateWorkspaceScaleCommandV1,
        onSucceedUpdateWorkspaceScaleCommandV1,
        validationErrorsUpdateWorkspaceScaleCommandV1
    } = useUpdateWorkspaceScaleCommandV1(workspaceId)


    // ======================
    // Stable ViewModel
    // ======================

    const discreteScaleEditor = reactive(new DiscreteScaleEditorViewModel())


    // ======================
    // Derived State
    // ======================

    const workspaceName = computed(() => rm.value?.WorkspaceName ?? null)

    const scales = computed<DiscreteScaleReadModel[]>(() => rm.value?.Scales ?? [])

    const scalesView = computed<DiscreteScaleReadModel[]>(() =>
        [...scales.value].sort((a, b) => strCompareLowerCase(a.Name, b.Name))
    )

    const tchTemplates = computed<DiscreteScaleReadModel[] | null>(
        () => tchTemplatesModel.value?.Templates ?? null
    )


    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = "Scales | TransparentChoice"
        runWorkspaceScalesListQueryV1()
        runTchScaleTemplatesListQueryV1()
    })


    // ======================
    // UI Actions
    // ======================

    function showScaleCreator(tchTemplate: DiscreteScaleReadModel | null)
    {
        if (tchTemplate)
        {
            const scale = { ...tchTemplate }

            discreteScaleEditor.showCreator(
                scale.Name,
                scale.Description,
                scale.Levels
            )
        }
        else
        {
            const name = generateName("Scale")

            discreteScaleEditor.showCreator(
                name,
                null,
                [
                    new DiscreteScaleLevelReadModel(),
                    new DiscreteScaleLevelReadModel(),
                    new DiscreteScaleLevelReadModel()
                ]
            )
        }
    }

    function showScaleEditor(model: DiscreteScaleReadModel)
    {
        discreteScaleEditor.showEditor(
            model.Id,
            model.Name,
            model.Description,
            model.Levels
        )
    }


    // ======================
    // Command Handlers
    // ======================

    function handleCreateWorkspaceScaleCommand(name: string, description: string | null, levels: string)
    {
        invokeCreateWorkspaceScaleCommandV1({
            Name: name,
            Description: description,
            LevelsSerialized: levels
        })
    }

    function handleDeleteWorkspaceScaleCommand(scaleId: number)
    {
        invokeDeleteWorkspaceScaleCommandV1({
            Id: scaleId
        })
    }

    function handleUpdateWorkspaceScaleCommand(scaleId: number, name: string, description: string | null, levels: string)
    {
        invokeUpdateWorkspaceScaleCommandV1({
            Id: scaleId,
            Name: name,
            Description: description,
            LevelsSerialized: levels
        })
    }

    // ======================
    // Success Watchers
    // ======================

    onSucceedCreateWorkspaceScaleCommandV1(() =>
    {
        runWorkspaceScalesListQueryV1()
        discreteScaleEditor.close()
    })

    onSucceedDeleteWorkspaceScaleCommandV1(() =>
    {
        runWorkspaceScalesListQueryV1()
        discreteScaleEditor.close()
    })

    onSucceedUpdateWorkspaceScaleCommandV1(() =>
    {
        runWorkspaceScalesListQueryV1()
        discreteScaleEditor.close()
    })

    // ======================
    // Failed Watchers
    // ======================

    watch(validationErrorsCreateWorkspaceScaleCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return    
        discreteScaleEditor.displayValidationErrors(validationErrors)
    })

    watch(validationErrorsUpdateWorkspaceScaleCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return    
        discreteScaleEditor.displayValidationErrors(validationErrors)
    })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(DiscreteScaleEditor, {
      viewModel: discreteScaleEditor,
      onCreateScale: handleCreateWorkspaceScaleCommand,
      onUpdateScale: handleUpdateWorkspaceScaleCommand,
      onDeleteScale: handleDeleteWorkspaceScaleCommand
    }, null, 8 /* PROPS */, ["viewModel"]),
    _createVNode(WorkspaceNavigation, {
      workspaceId: workspaceId.value,
      workspaceName: workspaceName.value
    }, null, 8 /* PROPS */, ["workspaceId", "workspaceName"]),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _cache[6] || (_cache[6] = _createElementVNode("div", { class: "col-lg-6" }, [
          _createElementVNode("h1", { style: {"font-size":"2em","font-weight":"300","color":"#33475b","margin":"0"} }, "Scales")
        ], -1 /* CACHED */)),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _cache[5] || (_cache[5] = _createElementVNode("button", {
              class: "btn btn-success dropdown-toggle",
              style: {"padding":"0.7em 2em"},
              type: "button",
              "data-toggle": "dropdown"
            }, " Create scale ", -1 /* CACHED */)),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("a", {
                class: "dropdown-item my-2 dropdown-item-template",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (showScaleCreator(null)))
              }, [...(_cache[1] || (_cache[1] = [
                _createElementVNode("span", null, "Blank", -1 /* CACHED */),
                _createElementVNode("br", null, null, -1 /* CACHED */),
                _createElementVNode("small", { class: "text-muted" }, "Create scale template from scratch", -1 /* CACHED */)
              ]))]),
              _cache[4] || (_cache[4] = _createElementVNode("div", { class: "dropdown-divider" }, null, -1 /* CACHED */)),
              (tchTemplates.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _cache[3] || (_cache[3] = _createElementVNode("h6", { class: "dropdown-header" }, "TransparentChoice's Templates", -1 /* CACHED */)),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tchTemplates.value, (t) => {
                      return (_openBlock(), _createElementBlock("a", {
                        class: "dropdown-item dropdown-item-template my-2",
                        onClick: ($event: any) => (showScaleCreator(t))
                      }, [
                        _createElementVNode("span", null, _toDisplayString(t.Name), 1 /* TEXT */),
                        _cache[2] || (_cache[2] = _createElementVNode("br", null, null, -1 /* CACHED */)),
                        _createElementVNode("small", _hoisted_8, _toDisplayString(t.Description), 1 /* TEXT */)
                      ], 8 /* PROPS */, _hoisted_7))
                    }), 256 /* UNKEYED_FRAGMENT */))
                  ]))
                : _createCommentVNode("v-if", true)
            ])
          ])
        ])
      ]),
      _cache[8] || (_cache[8] = _createElementVNode("hr", { style: {"border-color":"#bbb","margin":"20px 0 60px"} }, null, -1 /* CACHED */)),
      (scales.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(scalesView.value, (s) => {
                return (_openBlock(), _createElementBlock("div", {
                  class: "col-sm-6 col-md-4",
                  key: s.Id
                }, [
                  _createElementVNode("div", _hoisted_11, [
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("a", {
                        onClick: ($event: any) => (showScaleEditor(s))
                      }, [...(_cache[7] || (_cache[7] = [
                        _createElementVNode("i", { class: "fa fa-pencil" }, null, -1 /* CACHED */)
                      ]))], 8 /* PROPS */, _hoisted_13)
                    ]),
                    _createElementVNode("h4", _hoisted_14, [
                      _createElementVNode("span", null, _toDisplayString(s.Name), 1 /* TEXT */)
                    ]),
                    _createElementVNode("small", _hoisted_15, _toDisplayString(s.LevelsDescription), 1 /* TEXT */)
                  ])
                ]))
              }), 128 /* KEYED_FRAGMENT */))
            ])
          ]))
        : _createCommentVNode("v-if", true)
    ])
  ]))
}
}

})